import * as Sentry from '@sentry/react'
import React, { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE, IntegrationServiceId } from '../../common/integrations'
import { SegmentedExternalContactUpdateBody } from './SegmentedExternalContactUpdateBody'
import { DefaultExternalContactUpdateBody } from './DefaultExternalContactUpdateBody'
import { useAppSelector } from '../../store/hooks'
import {
  selectHistoryServiceId,
  selectHistoryContactValues,
  selectHistoryPreviousContactValues,
  selectHistoryContactSchema,
  selectHistorySyncStatus,
} from '../../store/meetingSummary/contactUpdate'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetContactSchemaQuery } from '../../store/integrations/integrations'
import { ContactUpdateFormValues } from './models'
import { ContactUpdateHistoryProvider } from './ContactUpdateHistoryContext'
import { useContactUpdateHistory } from './ContactUpdateHistoryTypes'
import { getHistoryFilteredContactValues } from './utils'
import { ExternalUpdateHistoryHeader } from './ExternalUpdateHistoryHeader'

interface ExternalUpdateHistoryViewProps {
  serviceId?: IntegrationServiceId
}

const ExternalUpdateHistoryContent: React.FC<ExternalUpdateHistoryViewProps> = ({ serviceId: propServiceId }) => {
  const historyServiceId = useAppSelector(selectHistoryServiceId)
  const historyContactValues = useAppSelector(selectHistoryContactValues)
  const historyPreviousContactValues = useAppSelector(selectHistoryPreviousContactValues)
  const historyContactSchema = useAppSelector(selectHistoryContactSchema)
  const historySyncStatus = useAppSelector(selectHistorySyncStatus)
  const accountId = useAppSelector(selectAccountId)
  const form = useForm<ContactUpdateFormValues>()
  const context = useContactUpdateHistory()
  const [activeSectionId, setActiveSectionId] = React.useState<string | undefined>()

  const serviceId = propServiceId || historyServiceId

  const { data: contactSchemaResponse } = useGetContactSchemaQuery(
    {
      accountId,
      connector: serviceId!,
      context: { objectName: 'contact' },
    },
    {
      skip: !!historyContactSchema || !serviceId,
    },
  )

  const setHistoryData = context?.setHistoryData
  const clearHistoryMode = context?.clearHistoryMode

  useEffect(() => {
    if (!setHistoryData || !clearHistoryMode) {
      return
    }

    const schemaToUse = historyContactSchema || contactSchemaResponse?.data

    if (historyContactValues && schemaToUse && serviceId) {
      try {
        const filteredContactValues = getHistoryFilteredContactValues(historyContactValues)

        setHistoryData({
          contactValues: filteredContactValues,
          previousContactValues: historyPreviousContactValues,
          contactSchema: schemaToUse,
          serviceId,
        })
      } catch (error) {
        Sentry.captureException(error)
        console.error('Failed to process contact history data')
        clearHistoryMode()
      }
    } else {
      clearHistoryMode()
    }
  }, [setHistoryData, clearHistoryMode, historyContactValues, historyPreviousContactValues, historyContactSchema, contactSchemaResponse?.data, serviceId])

  if (!context) {
    return null
  }

  if (!serviceId) {
    return null
  }

  const isSegmented = INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE.includes(serviceId)
  const ExternalContactUpdateView = isSegmented ? SegmentedExternalContactUpdateBody : DefaultExternalContactUpdateBody

  return (
    <FormProvider {...form}>
      <div className='h-100 d-flex flex-column'>
        {historySyncStatus && <ExternalUpdateHistoryHeader syncStatus={historySyncStatus} />}
        <div className='flex-grow-1 overflow-auto'>
          <ExternalContactUpdateView
            serviceId={serviceId}
            readonly={true}
            enableExplainer={false}
            activeSectionId={activeSectionId}
            onActiveSectionIdChange={setActiveSectionId}
          />
        </div>
      </div>
    </FormProvider>
  )
}

export const ExternalUpdateHistoryView: React.FC<ExternalUpdateHistoryViewProps> = (props) => {
  return (
    <ContactUpdateHistoryProvider>
      <ExternalUpdateHistoryContent {...props} />
    </ContactUpdateHistoryProvider>
  )
}
